/* eslint-disable import/exports-last */
import { useCallback, useEffect, useRef, useState } from 'react'

import type { RefObject } from 'react'

export type Sizes = {
  containerWidth: number | undefined
  rowHeightsMap: Map<number, number>
}

type UseResizeObserverOptions<T extends HTMLElement | null = HTMLElement> = {
  containerRef: RefObject<T>
  rowRefs: RefObject<Map<number, T>>
  onResize: (sizes: Sizes) => void
}

const initialSize: Sizes = {
  containerWidth: undefined,
  rowHeightsMap: new Map<number, number>(),
}

function getRowKeyHash<T extends HTMLElement | null = HTMLElement>(newRows: Map<number, T>) {
  return newRows.keys().reduce((sum, item) => sum + item, '')
}

function useResizeObserver<T extends HTMLElement | null = HTMLElement>(options: UseResizeObserverOptions<T>): Sizes {
  const { containerRef, rowRefs } = options
  const reverseRowRefs = useRef<Map<T, number>>(new Map([...rowRefs.current].map(([key, value]) => [value, key])))
  const prevRowRefs = useRef<Map<number, T>>(new Map())
  const previousSizesMap = useRef<Map<Element, number | string>>(new Map())
  const [containerWidth, setContainerWidth] = useState<Sizes['containerWidth']>(initialSize.containerWidth)
  const [rowHeightsMap, setRowHeightsMap] = useState<Sizes['rowHeightsMap']>(initialSize.rowHeightsMap)
  const hasChanged = useRef(false)
  const onResize = useRef<((size: Sizes) => void) | undefined>(undefined)
  onResize.current = options.onResize
  const observerRef = useRef<ResizeObserver | null>(null)
  const handleContainer = useCallback(
    (entry: ResizeObserverEntry) => {
      if (!containerRef || !containerRef.current) return

      if (!entry) return
      const newContainerWidth = extractSize(entry, 'contentBoxSize', 'inlineSize')
      const widthHasChanged =
        typeof newContainerWidth === 'number' && previousSizesMap.current.get(entry.target) !== newContainerWidth

      if (widthHasChanged) {
        hasChanged.current = true
        previousSizesMap.current.set(entry.target, newContainerWidth)
        setContainerWidth(newContainerWidth)
      }
    },
    [containerRef]
  )

  const handleRow = useCallback(
    (entry: ResizeObserverEntry) => {
      if (!rowRefs || !rowRefs.current) return

      if (!entry) return
      const newRowHeight = extractSize(entry, 'borderBoxSize', 'blockSize')
      const heightHasChanged =
        typeof newRowHeight === 'number' && previousSizesMap.current.get(entry.target) !== newRowHeight

      if (heightHasChanged) {
        hasChanged.current = true
        previousSizesMap.current.set(entry.target, newRowHeight)
        setRowHeightsMap((prev) => {
          const i = reverseRowRefs.current.get(entry.target as T)
          if (typeof i !== 'number') return prev

          const newRowHeightsMap = new Map(prev)
          newRowHeightsMap.set(i, newRowHeight)
          return newRowHeightsMap
        })
      }
    },
    [rowRefs]
  )

  useEffect(() => {
    if (!containerRef || !containerRef.current || !rowRefs || !rowRefs.current) return

    if (!window || typeof window === 'undefined' || !('ResizeObserver' in window)) return

    observerRef.current = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const handler = entry.target === containerRef.current ? handleContainer : handleRow
        handler(entry)
      }
    })

    observerRef.current.observe(containerRef.current, { box: 'content-box' })
    rowRefs.current.forEach((row, i) => {
      if (!row || !observerRef.current) return

      prevRowRefs.current.set(i, row)
      reverseRowRefs.current.set(row, i)
      observerRef.current.observe(row, { box: 'border-box' })
    })

    return () => {
      observerRef.current && observerRef.current.disconnect()
    }
  }, [containerRef, handleContainer, handleRow, rowRefs])

  const rowKeyHash = getRowKeyHash(rowRefs.current)

  useEffect(() => {
    prevRowRefs.current.entries().forEach(([key, value]) => {
      if (value && !rowRefs.current.has(key)) {
        observerRef.current?.unobserve(value)
        reverseRowRefs.current.delete(value)
      }
    })

    rowRefs.current.entries().forEach(([key, value]) => {
      if (value && !prevRowRefs.current.has(key)) {
        observerRef.current?.observe(value, { box: 'border-box' })
        reverseRowRefs.current.set(value, key)
      }
    })

    prevRowRefs.current = new Map(rowRefs.current)
  }, [rowRefs, rowKeyHash])

  if (hasChanged.current && onResize.current) {
    onResize.current({ containerWidth, rowHeightsMap })
    hasChanged.current = false
  }

  return { containerWidth, rowHeightsMap }
}

type BoxSizesKey = keyof Pick<ResizeObserverEntry, 'borderBoxSize' | 'contentBoxSize' | 'devicePixelContentBoxSize'>

function extractSize(
  entry: ResizeObserverEntry,
  box: BoxSizesKey,
  sizeType: 'inlineSize' | 'blockSize'
): number | undefined {
  const boxSize = entry[box] as ResizeObserverSize[] | ResizeObserverSize | undefined

  if (Array.isArray(boxSize) && boxSize.length > 0) {
    return boxSize[0]?.[sizeType]
  }

  if (boxSize && typeof boxSize === 'object' && sizeType in boxSize) {
    return (boxSize as ResizeObserverSize)[sizeType]
  }

  // Fallback to contentRect for better browser support
  return sizeType === 'inlineSize' ? entry.contentRect.width : entry.contentRect.height
}

export default useResizeObserver
